import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, AuthMeResponse } from '../types/auth';
import { fetchAuthMe, logoutUser } from '../api/auth';
import { setApiHandlers } from '../api/client';
import { useToast } from './ToastContext';

interface AuthContextType {
  user: User | null;
  authenticated: boolean;
  loading: boolean;
  setupRequired: boolean;
  refreshAuth: () => Promise<AuthMeResponse | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [setupRequired, setSetupRequired] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { showToast } = useToast();

  const handleUnauthorized = useCallback(() => {
    setAuthenticated(false);
    setUser(null);
  }, []);

  const handleConflict = useCallback((detail: string) => {
    showToast(detail, 'warning', '版本冲突');
  }, [showToast]);

  useEffect(() => {
    setApiHandlers({
      onUnauthorized: handleUnauthorized,
      onConflict: handleConflict,
    });
  }, [handleUnauthorized, handleConflict]);

  const refreshAuth = useCallback(async (): Promise<AuthMeResponse | null> => {
    try {
      const data = await fetchAuthMe();
      setAuthenticated(data.authenticated);
      setUser(data.user || null);
      setSetupRequired(!!data.setup_required);
      return data;
    } catch {
      setAuthenticated(false);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
      setAuthenticated(false);
      setUser(null);
      showToast('已安全退出登录', 'info');
    } catch (err: any) {
      showToast(err.message || '退出登录失败', 'error');
    }
  }, [showToast]);

  return (
    <AuthContext.Provider
      value={{
        user,
        authenticated,
        loading,
        setupRequired,
        refreshAuth,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
